import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, OnDestroy, ViewChild, effect, input } from '@angular/core';
import { EditorState } from '@codemirror/state';
import { basicSetup, EditorView as CodeMirrorEditorView } from 'codemirror';
import { syntaxHighlighting, defaultHighlightStyle } from '@codemirror/language';
import { javascript } from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';
import { html } from '@codemirror/lang-html';
import { css } from '@codemirror/lang-css';
import { markdown } from '@codemirror/lang-markdown';
import { python } from '@codemirror/lang-python';
import { xml } from '@codemirror/lang-xml';
import { yaml } from '@codemirror/lang-yaml';
import { defaultMarkdownParser, schema } from 'prosemirror-markdown';
import { Schema } from 'prosemirror-model';
import { EditorState as ProseMirrorEditorState } from 'prosemirror-state';
import { EditorView as ProseMirrorEditorView } from 'prosemirror-view';
import { SelectButtonComponent } from 'src/app/@core/components/ui/select-button/select-button.component';
import { IAttachment } from 'src/app/@core/models/model';
import { createExternalRequestContext } from 'src/app/@core/utils/http-context.utils';
import { DocumentPreviewComponent } from '../document-preview/document-preview.component';
import { PdfViewerComponent } from '../pdf-viewer/pdf-viewer.component';
import { SegmentedOption } from 'src/app/@core/components/ui/segmented/segmented.component';
import { MarkdownRenderPipe } from 'src/app/@shared/pipes/markdown-render.pipe';
import { DynamicComponentRenderer } from 'src/app/@shared/directives/dynamic-component-renderer.directive';

export enum PreviewType {
  CANVAS = 'CANVAS',
  MARKDOWN = 'MARKDOWN',
  HTML = 'HTML',
  CODE_MIRROR = 'CODE_MIRROR',
  JSON = 'JSON',
  PDF_VIEWER = 'PDF_VIEWER',
  ONLY_OFFICE = 'ONLY_OFFICE',
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
}

export enum HtmlViewMode {
  RENDERED = 'RENDERED',
  CODE = 'CODE'
}

@Component({
  selector: 'app-multi-format-preview',
  imports: [CommonModule, FormsModule, DocumentPreviewComponent, PdfViewerComponent, SelectButtonComponent, MarkdownRenderPipe, DynamicComponentRenderer],
  templateUrl: './multi-format-preview.component.html',
  styleUrl: './multi-format-preview.component.scss'
})
export class MultiFormatPreviewComponent implements OnDestroy {
  documentId = input<string>('');
  file = input<IAttachment>();
  attachment: IAttachment = {} as IAttachment;
  pageNumber = input<number>(1);
  /** When true, heavy previews (like PDFs) should temporarily reduce work during container resizing. */
  isResizing = input<boolean>(false);
  loading = input<boolean>(false);

  imageScrollable = input<boolean>(false);
  @ViewChild('codeEditorContainer', { static: false }) codeEditorContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('canvasContainer', { static: false }) canvasContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('previewIframe', { static: false }) previewIframe!: ElementRef<HTMLIFrameElement>;
  private codeEditorView: CodeMirrorEditorView | null = null;
  private proseMirrorView: ProseMirrorEditorView | null = null;
  private proseMirrorSchema = new Schema({
    nodes: schema.spec.nodes,
    marks: schema.spec.marks,
  });
  previewType: PreviewType | undefined;
  PreviewType = PreviewType;
  htmlViewMode: HtmlViewMode = HtmlViewMode.RENDERED;
  HtmlViewMode = HtmlViewMode;
  textContent: string = '';
  isLoadingText: boolean = false;
  jsonHighlighted: string = '';
  jsonLineCount: number = 0;
  jsonCopied: boolean = false;
  htmlViewModeOptions: SegmentedOption[] = [
    { value: HtmlViewMode.RENDERED, label: 'Rendered View', icon: 'pi pi-eye' },
    { value: HtmlViewMode.CODE, label: 'Code View', icon: 'pi pi-code' }
  ];

  constructor(private http: HttpClient) {
    effect(() => {
      const fileValue = this.file();
      if (fileValue) {
        this.attachment = fileValue;
        this.previewType = this.determinePreviewType(fileValue);
        if (this.previewType === PreviewType.HTML) {
          this.loadHtmlContent();
        } else if (this.previewType === PreviewType.CODE_MIRROR) {
          this.loadCodeContent();
        } else if (this.previewType === PreviewType.JSON) {
          this.loadJsonContent();
        } else if (this.previewType === PreviewType.MARKDOWN) {
          this.loadMarkdownContent();
        } else if (this.previewType === PreviewType.CANVAS) {
          this.loadCanvasContent();
        } else if (this.previewType === PreviewType.TEXT) {
          this.loadPlainTextContent();
        }
      }
    });
  }

  /**
   * Returns a CodeMirror language extension based on the attachment MIME type and/or filename extension.
   * If no language matches, returns null (CodeMirror will render plain text).
   */
  private getCodeMirrorLanguageExtension(): any | null {
    const mimeType = (this.attachment?.type || '').toLowerCase();
    const filename = (this.attachment?.filename || '').toLowerCase();
    const url = (this.attachment?.url || this.attachment?.fullUrl || '').toLowerCase();

    const extension = (() => {
      const source = filename || url;
      const parts = source.split('.');
      return parts.length > 1 ? (parts.pop() || '') : '';
    })();

    // MIME-based detection (prefer this when present)
    if (mimeType.includes('json')) return json();
    if (mimeType.includes('javascript') || mimeType.includes('ecmascript')) return javascript({ typescript: false, jsx: true });
    if (mimeType.includes('typescript')) return javascript({ typescript: true, jsx: true });
    if (mimeType.includes('html')) return html();
    if (mimeType.includes('css') || mimeType.includes('scss') || mimeType.includes('sass') || mimeType.includes('less')) return css();
    if (mimeType.includes('markdown')) return markdown();
    if (mimeType.includes('python')) return python();
    if (mimeType.includes('xml')) return xml();
    if (mimeType.includes('yaml') || mimeType.includes('yml')) return yaml();

    // Extension-based detection (fallback)
    switch (extension) {
      case 'js':
      case 'jsx':
        return javascript({ typescript: false, jsx: true });
      case 'ts':
      case 'tsx':
        return javascript({ typescript: true, jsx: true });
      case 'json':
        return json();
      case 'html':
      case 'htm':
        return html();
      case 'css':
      case 'scss':
      case 'sass':
      case 'less':
        return css();
      case 'md':
      case 'markdown':
        return markdown();
      case 'py':
        return python();
      case 'xml':
        return xml();
      case 'yaml':
      case 'yml':
        return yaml();
      default:
        return null;
    }
  }

  ngOnDestroy() {
    if (this.codeEditorView) {
      this.codeEditorView.destroy();
      this.codeEditorView = null;
    }
    if (this.proseMirrorView) {
      this.proseMirrorView.destroy();
      this.proseMirrorView = null;
    }
  }

  private loadTextContent(onSuccess?: () => void) {
    const url = this.attachment.fullUrl || this.attachment.url;
    if (!url) return;

    this.isLoadingText = true;

    this.http.get(url, {
      responseType: 'text',
      context: createExternalRequestContext()
    }).subscribe({
      next: (content) => {
        this.textContent = content;
        this.isLoadingText = false;
        onSuccess?.();
      },
      error: (error) => {
        console.error('Error loading content:', error);
        this.isLoadingText = false;
        this.textContent = 'Error loading content. Please try again.';
      }
    });
  }

  loadHtmlContent() {
    this.loadTextContent(() => {
      setTimeout(() => {
        this.updateIframeContent();
      }, 0);
    });
  }

  loadCodeContent() {
    this.loadTextContent(() => {
      setTimeout(() => this.initializeCodeEditor(), 0);
    });
  }

  loadCanvasContent() {
    this.loadTextContent(() => {
      setTimeout(() => this.initializeProseMirrorEditor(), 0);
    });
  }

  loadMarkdownContent() {
    // Markdown attachments should use the same renderer as chat (markdownRender pipe),
    // so we only need the raw text content.
    this.loadTextContent();
  }

  loadPlainTextContent() {
    this.loadTextContent();
  }

  loadJsonContent() {
    this.loadTextContent(() => {
      try {
        const parsed = JSON.parse(this.textContent);
        const pretty = JSON.stringify(parsed, null, 2);
        this.jsonLineCount = pretty.split('\n').length;
        this.jsonHighlighted = this.syntaxHighlightJson(pretty);
      } catch {
        // Not valid JSON — fall back to raw text with line count
        this.jsonLineCount = this.textContent.split('\n').length;
        this.jsonHighlighted = this.escapeHtml(this.textContent);
      }
    });
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private syntaxHighlightJson(json: string): string {
    const escaped = this.escapeHtml(json);
    return escaped.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      (match) => {
        let cls = 'json-number';
        if (/^"/.test(match)) {
          cls = /:$/.test(match) ? 'json-key' : 'json-string';
        } else if (/true|false/.test(match)) {
          cls = 'json-boolean';
        } else if (/null/.test(match)) {
          cls = 'json-null';
        }
        return `<span class="${cls}">${match}</span>`;
      }
    );
  }

  copyJsonToClipboard() {
    const text = this.textContent;
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      this.jsonCopied = true;
      setTimeout(() => (this.jsonCopied = false), 2000);
    });
  }

  setHtmlViewMode(mode: HtmlViewMode) {
    this.htmlViewMode = mode;
    if (mode === HtmlViewMode.RENDERED && this.textContent) {
      setTimeout(() => {
        this.updateIframeContent();
      }, 0);
    }
  }

  private updateIframeContent() {
    if (!this.previewIframe?.nativeElement || !this.textContent) {
      return;
    }

    try {
      const iframe = this.previewIframe.nativeElement;
      const doc = iframe.contentDocument || iframe.contentWindow?.document;

      if (!doc) {
        console.warn('Cannot access iframe document. It may not be fully loaded yet.');
        return;
      }

      iframe.srcdoc = this.textContent;

      // Ensure iframe body has proper styling for full height
      if (doc.body) {
        doc.body.style.margin = '0';
        doc.body.style.padding = '0';
        doc.body.style.height = '100%';
        doc.body.style.width = '100%';
      }

      // Ensure html element has full height
      if (doc.documentElement) {
        doc.documentElement.style.height = '100%';
        doc.documentElement.style.width = '100%';
      }
    } catch (error) {
      console.error('Error updating iframe content:', error);
    }
  }

  initializeCodeEditor() {
    if (!this.codeEditorContainer?.nativeElement || !this.textContent) {
      return;
    }

    // Destroy existing editor if any
    if (this.codeEditorView) {
      this.codeEditorView.destroy();
      this.codeEditorView = null;
    }

    // Create new editor
    const languageExtension = this.getCodeMirrorLanguageExtension();
    const state = EditorState.create({
      doc: this.textContent,
      extensions: [
        basicSetup,
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        ...(languageExtension ? [languageExtension] : []),
        EditorState.readOnly.of(true), // Make it read-only for preview
      ]
    });

    this.codeEditorView = new CodeMirrorEditorView({
      state,
      parent: this.codeEditorContainer.nativeElement
    });
  }

  initializeProseMirrorEditor() {
    if (!this.canvasContainer?.nativeElement || !this.textContent) {
      return;
    }

    // Destroy existing editor if any
    if (this.proseMirrorView) {
      this.proseMirrorView.destroy();
      this.proseMirrorView = null;
    }

    // Parse markdown content
    const contentValue = this.textContent.trim();
    const doc = contentValue
      ? defaultMarkdownParser.parse(contentValue)
      : this.proseMirrorSchema.node('doc', null, this.proseMirrorSchema.node('paragraph'));

    // Create read-only editor state
    const state = ProseMirrorEditorState.create({
      schema: this.proseMirrorSchema,
      doc,
    });

    // Create read-only editor view
    this.proseMirrorView = new ProseMirrorEditorView(this.canvasContainer.nativeElement, {
      state,
      editable: () => false, // Read-only
    });

    // Remove outline from editor element
    const contentEditableElements = this.canvasContainer.nativeElement.querySelectorAll('[contenteditable="true"]');
    contentEditableElements.forEach((el) => {
      const htmlEl = el as HTMLElement;
      htmlEl.style.outline = 'none';
      htmlEl.style.boxShadow = 'none';
    });
  }

  determinePreviewType(attachment: IAttachment): PreviewType | undefined {
    if (!attachment?.type && !attachment?.url) {
      return undefined;
    }

    const mimeType = attachment.type?.toLowerCase() || '';
    const filename = (attachment.filename || '').toLowerCase();
    let extension = '';

    if (attachment.url) {
      const urlParts = attachment.url.split('.');
      extension = urlParts.length > 1 ? urlParts.pop()?.toLowerCase() || '' : '';
    }

    // Check for image types first
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico'];
    if (mimeType.includes('image') || imageExtensions.includes(extension)) {
      return PreviewType.IMAGE;
    }

    // Markdown attachments:
    // - Keep existing ProseMirror "canvas" rendering for canvas-export markdown files (heuristic).
    // - Otherwise use the same markdown renderer as the left chat so tables (GFM) work.
    if (mimeType === 'text/markdown' || extension === 'md') {
      const isCanvasMarkdown = filename.includes('canvas') || extension === 'md' && (attachment.url || '').toLowerCase().includes('canvas');
      return isCanvasMarkdown ? PreviewType.CANVAS : PreviewType.MARKDOWN;
    }

    if (mimeType === 'text/html' || extension === 'html' || extension === 'htm') {
      return PreviewType.HTML;
    }

    if (mimeType.includes('application/json') || mimeType.includes('text/json') || extension === 'json') {
      return PreviewType.JSON;
    }

    const codeExtensions = ['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'cs', 'php', 'rb', 'go', 'rs', 'swift', 'kt', 'scala', 'sh', 'bash', 'zsh', 'yaml', 'yml', 'xml', 'css', 'scss', 'sass', 'less', 'vue', 'svelte'];
    const codeMimeTypes = ['text/javascript', 'application/javascript', 'text/x-python', 'application/x-python-code', 'text/x-java-source', 'text/x-c++src', 'text/x-csrc', 'text/x-php', 'application/x-ruby', 'text/x-go', 'application/json', 'application/xml', 'text/css', 'text/x-scss'];

    if (codeMimeTypes.some(type => mimeType.includes(type)) || codeExtensions.includes(extension)) {
      return PreviewType.CODE_MIRROR;
    }

    if (mimeType === 'application/pdf' || extension === 'pdf') {
      return PreviewType.PDF_VIEWER;
    }

    if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      mimeType === 'application/msword' ||
      extension === 'doc' || extension === 'docx') {
      return PreviewType.ONLY_OFFICE;
    }

    if (mimeType === 'application/vnd.ms-excel' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      extension === 'xls' || extension === 'xlsx') {
      return PreviewType.ONLY_OFFICE;
    }

    if (mimeType === 'text/csv' || mimeType === 'application/csv' || extension === 'csv') {
      return PreviewType.ONLY_OFFICE;
    }

    if (mimeType === 'application/vnd.ms-powerpoint' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
      extension === 'ppt' || extension === 'pptx') {
      return PreviewType.ONLY_OFFICE;
    }

    if (mimeType === 'text/plain' || extension === 'txt') {
      return PreviewType.TEXT;
    }

    return undefined;
  }
}
